import React from 'react';
import styled from 'styled-components';
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { CampaignAnalytics } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { campaignChannelLabel, campaignGoalLabel, formatCampaignDateTime, getCampaignStatusTranslationKey } from '../campaignsUtils';
import AppButton from '../../../components/AppButton';

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const Notice = styled.div`
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--surface-border-subtle);
    border-radius: var(--spacer-XS);
    background: var(--surface-brand-soft);
    color: var(--text-body);
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacer-S);

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryCard = styled.div`
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: white;
`;

const SummaryLabel = styled.div`
    color: var(--text-muted);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
`;

const SummaryValue = styled.div`
    margin-top: var(--spacer-XXS);
    color: var(--text-body);
    font-size: 0.98rem;
    font-weight: 700;
`;

const SectionTitle = styled.h4`
    margin: 0;
    color: var(--text-body);
    font-size: 1rem;
    font-weight: 700;
`;

const SectionNote = styled.div`
    color: var(--text-muted);
    font-size: 0.9rem;
`;

interface CampaignReTriggerReviewModalProps {
    isOpen: boolean;
    analytics?: CampaignAnalytics;
    loading: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

export const CampaignReTriggerReviewModal: React.FC<CampaignReTriggerReviewModalProps> = ({
    isOpen,
    analytics,
    loading,
    onClose,
    onConfirm,
}) => {
    const t = useTranslations();

    if (!analytics) {
        return null;
    }

    const latestProcess = analytics.latestProcessDetails;

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                {t(TK.campaignAnalyticsReTriggerReviewTitle)}
            </ModalHeader>
            <ModalBody>
                <Content>
                    <Notice>
                        {t(TK.campaignAnalyticsReTriggerReviewDescription)}
                    </Notice>

                    <div>
                        <SectionTitle>{t(TK.campaignAnalyticsCurrentCampaign)}</SectionTitle>
                        <SummaryGrid style={{ marginTop: 'var(--spacer-S)' }}>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.campaignName)}</SummaryLabel>
                                <SummaryValue>{analytics.campaignName}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.channel)}</SummaryLabel>
                                <SummaryValue>{t(campaignChannelLabel(analytics.channel))}</SummaryValue>
                            </SummaryCard>
                            {analytics.goal != null && (
                                <SummaryCard>
                                    <SummaryLabel>{t(TK.goal)}</SummaryLabel>
                                    <SummaryValue>{t(campaignGoalLabel(analytics.goal))}</SummaryValue>
                                </SummaryCard>
                            )}
                            <SummaryCard>
                                <SummaryLabel>{t(TK.status)}</SummaryLabel>
                                <SummaryValue>{t(getCampaignStatusTranslationKey(analytics.status))}</SummaryValue>
                            </SummaryCard>
                        </SummaryGrid>
                    </div>

                    <div>
                        <SectionTitle>{t(TK.campaignAnalyticsLastSendSnapshot)}</SectionTitle>
                        <SectionNote style={{ marginTop: 'var(--spacer-XXS)' }}>
                            {t(TK.campaignAnalyticsAudienceWillBeRecomputed)}
                        </SectionNote>
                        <SummaryGrid style={{ marginTop: 'var(--spacer-S)' }}>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.recipients)}</SummaryLabel>
                                <SummaryValue>{latestProcess?.recipientCount ?? analytics.totalRecipients}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.sent)}</SummaryLabel>
                                <SummaryValue>{latestProcess?.sentCount ?? analytics.totalSent}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.failed)}</SummaryLabel>
                                <SummaryValue>{latestProcess?.failedCount ?? analytics.totalFailed}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>{t(TK.campaignLastTriggered)}</SummaryLabel>
                                <SummaryValue>{analytics.lastTriggeredAt ? formatCampaignDateTime(analytics.lastTriggeredAt) : t(TK.campaignNeverSent)}</SummaryValue>
                            </SummaryCard>
                        </SummaryGrid>
                    </div>
                </Content>
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={onClose} disabled={loading}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton
                    color="primary"
                    onClick={onConfirm}
                    disabled={loading}
                    style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}
                >
                    {loading ? <Spinner size="sm" style={{ marginRight: '0.5rem' }} /> : null}
                    {t(TK.campaignAnalyticsConfirmSendAgain)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};
